import type { XrefPumps } from "@/composables/forms/formTypes";
import type { Maybe } from "graphql/jsutils/Maybe";
import {
  getNominalPressureHeadFt,
  pump_gas_lock_ratio,
  pump_specific_speed
} from "@/libs/calculations/pumps";
import {
  DEFAULT_ESP_PUMP_RPM,
  DEFAULT_HPS_PUMP_RPM
} from "@/libs/calculations/constants";
import type { RecommendedOperatingRangeData, PumpDefaultData } from "./types";

export function getFrequencyRatio(
  operatingRPM: number,
  isScenarioEspShore: boolean
): number {
  return (
    operatingRPM /
    (isScenarioEspShore ? DEFAULT_ESP_PUMP_RPM : DEFAULT_HPS_PUMP_RPM)
  );
}

export function getPumpCurveFrequencyRatio(operatingRPM: number): number {
  return operatingRPM / DEFAULT_ESP_PUMP_RPM;
}

export function getPumpTorqueFtLbs(pumpRPM: number, pumpHp: number): number {
  return (pumpHp * 5852) / pumpRPM;
}

export function getPumpNpshr(
  xrefPump: Maybe<XrefPumps[number]>,
  flowRateBBLD: number,
  frequencyRatio: number
): number {
  if (!xrefPump?.r_pump) {
    return 0;
  }
  const npsh1 = xrefPump?.r_pump?.npsh1 || 0;
  const npsh2 = xrefPump?.r_pump?.npsh2 || 0;
  const npsh3 = xrefPump?.r_pump?.npsh3 || 0;
  const npsh4 = xrefPump?.r_pump?.npsh4 || 0;

  return (
    (npsh1 +
      npsh2 * flowRateBBLD +
      npsh3 * Math.pow(flowRateBBLD, 2) +
      npsh4 * Math.pow(flowRateBBLD, 3)) *
    Math.pow(frequencyRatio, 2)
  );
}

export function getRORForRPM(
  xrefPump: Maybe<XrefPumps[number]>,
  rpm = DEFAULT_ESP_PUMP_RPM,
  isScenarioEspShore: boolean
): RecommendedOperatingRangeData {
  const rpm_ratio =
    rpm / (isScenarioEspShore ? DEFAULT_ESP_PUMP_RPM : DEFAULT_HPS_PUMP_RPM);
  return {
    rorMin:
      (xrefPump?.r_pump?.min_rate_standard_pump_bpd || 0) *
      rpm_ratio *
      (xrefPump?.rate_correction_ratio || 1),
    rorMax:
      (xrefPump?.r_pump?.max_rate_standard_pump_bpd || 0) *
      rpm_ratio *
      (xrefPump?.rate_correction_ratio || 1),
    rorBep:
      (xrefPump?.r_pump?.bep_rate_standard_pump_bpd || 0) *
      rpm_ratio *
      (xrefPump?.rate_correction_ratio || 1),
    graphMax:
      (xrefPump?.r_pump?.max_graph_rate_standard_pump_bpd || 0) *
      rpm_ratio *
      (xrefPump?.rate_correction_ratio || 1)
  };
}

export function getOutsideROR(
  rorMin: number,
  rorMax: number,
  qInt = 0,
  qDisch = 0
): number {
  const qMax = Math.max(qInt, qDisch);
  const qMin = Math.min(qInt, qDisch);

  if (!rorMin || !rorMax) return 0;

  const minOutsideROR = qMin < rorMin ? (rorMin - qMin) / rorMin : 0;
  const maxOutsideROR = qMax > rorMax ? (qMax - rorMax) / rorMax : 0;

  return Math.max(minOutsideROR, maxOutsideROR);
}

export function getRequiredStagesForPump(
  xrefPump: XrefPumps[number],
  remainingRequiredLiftHeadFt: number,
  producedHeadFt = 0
): number {
  const headPerStage = producedHeadFt / (xrefPump.total_stages || 1);
  if (headPerStage === 0) return 0;
  const requiredStages = remainingRequiredLiftHeadFt / headPerStage;
  return Math.round(requiredStages);
}

export function getDefaultPumpData(xrefPumps: XrefPumps): PumpDefaultData {
  const result = {
    pumps: xrefPumps.reduce<PumpDefaultData["pumps"]>((data, xrefPump) => {
      data[xrefPump.id] = {
        gasLockRatio: 0
      };
      return data;
    }, {})
  };

  xrefPumps.forEach(xrefPump => {
    const pump = xrefPump.r_pump;

    if (!pump || !pump.bep_rate_standard_pump_bpd) {
      return;
    }

    result.pumps[xrefPump.id].gasLockRatio = getGasLockRatioForPump(xrefPump);
  });

  return result;
}

export function getGasLockRatioForPump(xrefPump: XrefPumps[number]): number {
  const pump = xrefPump.r_pump;

  if (!pump || !pump.bep_rate_standard_pump_bpd) {
    return 0;
  }

  const testPressureHeadFt = getNominalPressureHeadFt(
    pump.bep_rate_standard_pump_bpd,
    pump.hb,
    pump.h1,
    pump.h2,
    pump.h3,
    pump.h4,
    pump.h5
  );
  const specificSpeedRPM = pump_specific_speed(
    pump.bep_rate_standard_pump_bpd,
    testPressureHeadFt
  );

  return pump_gas_lock_ratio(specificSpeedRPM);
}
